import {useEffect, useMemo, useState} from "react";
import type {MouseEvent, ReactNode} from "react";
import {getOperationsStatus, unavailableStatus, type OperationsStatus} from "./api/client";
import {useAuth} from "./auth/AuthContext";
import {StatusRail} from "./components/StatusRail";
import {DashboardFoundation} from "./pages/DashboardFoundation";
import {ExecutionCenter} from "./pages/executioncenter/ExecutionCenter";
import {FlowTrace} from "./pages/FlowTrace";
import {NotFound} from "./pages/NotFound";
import {ProgramRegistry} from "./pages/programs/ProgramRegistry";
import {ProgramRoadmap} from "./pages/programs/ProgramRoadmap";
import {ProductRegistry} from "./pages/products/ProductRegistry";
import {CapabilityRegistry} from "./pages/capabilities/CapabilityRegistry";
import {ObjectWorkspace} from "./pages/workspaces/ObjectWorkspace";
import {
  ApprovedToolAdministration, LiveWorkerRegistry, ModelAdministration,
  ProviderAdministration, WorkerAdministration
} from "./pages/ai/AiAdministration";
import {UserAdministration} from "./pages/administration/UserAdministration";
import {AutonomousFactory} from "./pages/autonomousfactory/AutonomousFactory";
import {BackupRecovery} from "./pages/administration/BackupRecovery";
import {
  Approvals, Audit, CockpitCompatibility, Evidence,
  ExecutionPlanning, Operations, Settings, WorkPackages
} from "./pages/Pages";
import {isRouteActive, navigationGroups, resolveRoute, routes, type RouteMatch} from "./routing/routes";

const initialStatus: OperationsStatus = unavailableStatus("status not yet observed");

export function App() {
  const auth = useAuth();
  const [status, setStatus] = useState(initialStatus);
  const [location, setLocation] = useState(currentLocation);
  const [navigationOpen, setNavigationOpen] = useState(false);
  const match = useMemo(() => resolveRoute(location), [location]);
  useEffect(() => {
    if (!auth.user) return;
    const controller = new AbortController();
    void getOperationsStatus(auth.authorizedFetch, controller.signal)
      .then(setStatus)
      .catch(() => setStatus(unavailableStatus("backend status unavailable")));
    return () => controller.abort();
  }, [auth.user, auth.authorizedFetch]);
  useEffect(() => {
    const update = () => setLocation(currentLocation());
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  useEffect(() => {
    if (!match.redirectTo) return;
    window.history.replaceState({compatibilityRedirect: location}, "", match.redirectTo);
    setLocation(currentLocation());
  }, [location, match.redirectTo]);
  useEffect(() => {
    document.title = match.route.id === "not-found"
      ? "Not found · Inteacc AI Software Factory"
      : `${match.route.label} · Inteacc AI Software Factory`;
  }, [match.route.id, match.route.label]);
  const navigate = (event: MouseEvent<HTMLAnchorElement>, to: string) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    window.history.pushState({}, "", to);
    setLocation(currentLocation());
    setNavigationOpen(false);
    window.requestAnimationFrame(() => document.getElementById("main-content")?.focus());
  };
  if (auth.loading) {
    return <main className="auth-state"><h1>Establishing secure session</h1></main>;
  }
  if (!auth.user) {
    return <main className="auth-state"><h1>Authentication required</h1>
      <p>The Software Factory uses Authorization Code with PKCE S256.</p>
      <button type="button" onClick={() => void auth.login()}>Sign in with Keycloak</button>
    </main>;
  }
  return <div className="app-shell">
    <a className="skip-link" href="#main-content">Skip to main content</a>
    <aside className="sidebar">
      <div className="brand"><span className="brand-mark">IA</span><div>
        <strong>Inteacc AI Software Factory</strong><small>Governed engineering workspace</small>
      </div><button className="mobile-nav-toggle" type="button"
        aria-controls="primary-navigation" aria-expanded={navigationOpen}
        onClick={() => setNavigationOpen(open => !open)}>
        {navigationOpen ? "Close menu" : "Menu"}
      </button></div>
      <nav id="primary-navigation" aria-label="Primary"
        className={navigationOpen ? "navigation-open" : undefined}>
        {navigationGroups.map(group => <div className="nav-group" key={group}>
          <p>{group}</p>
          {routes.filter(route => route.navigation && route.group === group).map(route => {
            const active = isRouteActive(location, route);
            return <a key={route.id} href={route.pattern}
              onClick={event => navigate(event, route.pattern)}
              className={active ? "active" : undefined}
              aria-current={active ? "page" : undefined}>
              <span aria-hidden="true">{route.icon}</span>{route.label}
            </a>;
          })}
        </div>)}
      </nav>
      <div className="sidebar-foot"><span className="presence" aria-hidden="true" /><div>
        <strong>Governed execution</strong><small>Policy and human gates enforced</small>
      </div></div>
    </aside>
    <div className="workspace">
      <StatusRail status={status} user={auth.user} onLogout={auth.logout} />
      <main id="main-content" tabIndex={-1}>{pageFor(match, status)}</main>
    </div>
  </div>;
}

function pageFor(match: RouteMatch, status: OperationsStatus): ReactNode {
  switch (match.route.id) {
    case "dashboard": return <DashboardFoundation />;
    case "program-registry": return <ProgramRegistry />;
    case "program-roadmap": return <ProgramRoadmap programId={match.params.id} />;
    case "program-workspace": return <ObjectWorkspace objectType="PROGRAM" objectId={match.params.id} />;
    case "product-registry": return <ProductRegistry />;
    case "product-workspace": return <ObjectWorkspace objectType="PRODUCT" objectId={match.params.id} />;
    case "capability-registry": return <CapabilityRegistry />;
    case "capability-workspace": return <ObjectWorkspace objectType="CAPABILITY" objectId={match.params.id} />;
    case "work-package-registry": return <WorkPackages />;
    case "work-package-workspace": return <ObjectWorkspace objectType="WORK_PACKAGE" objectId={match.params.id} />;
    case "execution-plan-registry": return <ExecutionPlanning />;
    case "execution-plan-workspace": return <ObjectWorkspace objectType="EXECUTION_PLAN" objectId={match.params.id} />;
    case "execution-center": return <ExecutionCenter />;
    case "execution-center-run": return <ExecutionCenter runId={match.params.id} />;
    case "flow-trace": return <FlowTrace flowTraceId={match.params.id} />;
    case "autonomous-orchestration": return <AutonomousFactory view="ORCHESTRATION" />;
    case "action-center": return <AutonomousFactory view="ACTION_CENTER" />;
    case "policy-administration": return <AutonomousFactory view="POLICY" />;
    case "delivery-intelligence": return <AutonomousFactory view="DELIVERY" />;
    case "workers": return <LiveWorkerRegistry />;
    case "ai-providers": return <ProviderAdministration />;
    case "ai-models": return <ModelAdministration />;
    case "ai-workers": return <WorkerAdministration />;
    case "approved-tools": return <ApprovedToolAdministration />;
    case "approvals": return <Approvals />;
    case "evidence": return <Evidence />;
    case "audit": return <Audit />;
    case "operations": return <Operations status={status} />;
    case "administration-users": return <UserAdministration />;
    case "administration-backup-recovery": return <BackupRecovery />;
    case "administration-settings": return <Settings status={status} />;
    case "cockpit-boundary": return <CockpitCompatibility />;
    default: return <NotFound />;
  }
}

function currentLocation() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}
